import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Content, Form, Row, Col, Grid, FlexboxGrid } from 'rsuite';
import { updatePermitApplicationData } from '../../store/slices/apply/applySlice';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import {
  selectApplyApplicationData,
  selectConfigExpressPropertyDetailsQuestions,
} from '../../store/slices/config/configSelectors';
import { convertStringToKey } from '../../common/utils';
import { FLOWS_ENUM } from '../../common/enums';
import { FormQuestion } from 'src/components/form/FormQuestion/FormQuestion';

const ApplyPropertyDetails = () => {
  const dispatch = useDispatch();
  const form = useRef();
  const selectedPropertyDetailsQuestions = useSelector(
    selectConfigExpressPropertyDetailsQuestions
  );
  const { propertyDetails, property } = useSelector(selectApplyApplicationData);

  const initialFormValues = {
    ...propertyDetails,
    ...property,
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [formError, setFormError] = useState({});

  const formModel = useMemo(() => ({ ...formValues }), [formValues]);

  const onRadioQuestionGroupChanged = (value, _, questionKey) => {
    updateFormValues(questionKey, value);
  };

  const updateFormValues = (property, value) => {
    setFormValues((oldValues) => ({ ...oldValues, [property]: value }));
  };

  const prepareSupplementalQuestionsObject = () => {
    let supplementalQuestions = {};
    for (const question of selectedPropertyDetailsQuestions) {
      supplementalQuestions[question.key] = {
        question: question.title,
        questionSubtext: question.description,
        answer: formValues[question.key] || null, // api call expects there to be an answer
        ref: question.key,
      };
    }
    return supplementalQuestions;
  };

  const dispatchPropertyDetails = () => {
    if (!form?.current?.check()) return false;
    const supplementalQuestions = prepareSupplementalQuestionsObject();
    dispatch(
      updatePermitApplicationData({
        propertyDetails: {
          ...formValues,
          supplementalQuestions,
        },
      })
    );
    return true;
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.APPLY_LICENSE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            title="Property Details"
            onNext={dispatchPropertyDetails}
          >
            <Grid fluid>
              <Row>
                <Col>
                  <Text className="mb-24">
                    All fields must be completed before proceeding.
                  </Text>
                </Col>
              </Row>

              <Row className="mt-12 mb-24">
                <Col xs={24}>
                  <Form
                    ref={form}
                    checkTrigger="none"
                    formDefaultValue={formModel}
                    formValue={formValues}
                    onCheck={setFormError}
                    onChange={setFormValues}
                    fluid
                  >
                    <Grid
                      role="grid"
                      fluid
                    >
                      {selectedPropertyDetailsQuestions?.map(
                        (question, idx) => {
                          return (
                            <Row
                              className="mb-24"
                              key={`question_${convertStringToKey(question.key)}_${idx}`}
                            >
                              <Col xs={24}>
                                <FormQuestion
                                  question={question}
                                  formValues={formValues}
                                  formError={formError}
                                  updateFormValues={updateFormValues}
                                  onRadioQuestionGroupChanged={
                                    onRadioQuestionGroupChanged
                                  }
                                  initialFormValues={initialFormValues}
                                />
                              </Col>
                            </Row>
                          );
                        }
                      )}
                    </Grid>
                  </Form>
                </Col>
              </Row>
            </Grid>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ApplyPropertyDetails;
